import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import PWANavBar from './PWANavBar';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { PERMISSIONS } from '../config/permissions';

type NavItem = {
  name: string;
  href?: string;
  icon: string;
  show: boolean;
  children?: NavItem[];
};

export const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMenu = (name: string) => {
    setExpandedMenus((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: '📊', show: hasPermission(PERMISSIONS.DASHBOARD_DEFAULT) || hasPermission(PERMISSIONS.DASHBOARD_CONTENT) },
    {
      name: 'Products',
      icon: '📦',
      show: hasPermission(PERMISSIONS.PRODUCTS_VIEW) || hasPermission(PERMISSIONS.PRODUCTS_CREATE) || hasPermission(PERMISSIONS.PRODUCTS_STOCK_REPORT) || hasPermission(PERMISSIONS.MARKETING_COLLECTIONS_MANAGE),
      children: [
        { name: 'All Products', href: '/products', icon: '📦', show: hasPermission(PERMISSIONS.PRODUCTS_VIEW) },
        { name: 'Stock Report', href: '/products/stock-report', icon: '📉', show: hasPermission(PERMISSIONS.PRODUCTS_STOCK_REPORT) },
        { name: 'Create Product', href: '/products/create', icon: '➕', show: hasPermission(PERMISSIONS.PRODUCTS_CREATE) },
        { name: 'Product Drafts', href: '/products/drafts', icon: '📝', show: hasPermission(PERMISSIONS.PRODUCT_DRAFTS_VIEW) },
        { name: 'Marketing Collections', href: '/products/marketing-collections', icon: '🎯', show: hasPermission(PERMISSIONS.MARKETING_COLLECTIONS_MANAGE) },
        { name: 'Historical Mappings', href: '/historical-mappings', icon: '🔗', show: hasPermission(PERMISSIONS.HISTORICAL_SYNC_VIEW) },
        { name: 'Shopify Mappings', href: '/shopify-sync-config', icon: '🔄', show: hasPermission(PERMISSIONS.SHOPIFY_SYNC_CONFIG_MANAGE) },
      ],
    },
    {
      name: 'Product Settings',
      icon: '⚙️',
      show:
        hasPermission(PERMISSIONS.PRODUCT_TYPES_MANAGE) ||
        hasPermission(PERMISSIONS.PRODUCT_TAGS_MANAGE) ||
        hasPermission(PERMISSIONS.PRODUCT_OPTIONS_MANAGE) ||
        hasPermission(PERMISSIONS.CUSTOM_FIELDS_MANAGE),
      children: [
        { name: 'Product Types', href: '/product-types', icon: '🏷️', show: hasPermission(PERMISSIONS.PRODUCT_TYPES_MANAGE) },
        { name: 'Product Tags', href: '/product-tags', icon: '🔖', show: hasPermission(PERMISSIONS.PRODUCT_TAGS_MANAGE) },
        { name: 'Product Options', href: '/product-options', icon: '⚙️', show: hasPermission(PERMISSIONS.PRODUCT_OPTIONS_MANAGE) },
        { name: 'Product Attributes', href: '/custom-fields', icon: '🔧', show: hasPermission(PERMISSIONS.CUSTOM_FIELDS_MANAGE) },
      ],
    },
    { name: 'Orders', href: '/orders', icon: '🛒', show: hasPermission(PERMISSIONS.ORDERS_VIEW) },
    { name: 'Ledger', href: '/ledger', icon: '📒', show: hasPermission(PERMISSIONS.LEDGER_VIEW) },
    {
      name: 'Invoices',
      icon: '📄',
      show: hasPermission(PERMISSIONS.INVOICES_VIEW) || hasPermission(PERMISSIONS.INVOICES_GENERATE),
      children: [
        { name: 'All Invoices', href: '/invoices', icon: '📄', show: hasPermission(PERMISSIONS.INVOICES_VIEW) },
        { name: 'Generate Invoice', href: '/invoices/generate', icon: '➕', show: hasPermission(PERMISSIONS.INVOICES_GENERATE) },
      ],
    },
    { name: 'Order Discounts', href: '/order-discounts', icon: '🏷️', show: hasPermission(PERMISSIONS.DISCOUNTS_VIEW) },
    { name: 'Pick List', href: '/pickup-list', icon: '📋', show: hasPermission(PERMISSIONS.PICKUP_LIST_VIEW) },
    { name: 'Return List', href: '/return-list', icon: '↩️', show: hasPermission(PERMISSIONS.RETURN_LIST_VIEW) },
    { name: 'Process Return', href: '/returns/scan', icon: '📦', show: hasPermission(PERMISSIONS.ORDERS_RETURNS) },
    {
      name: 'CRM',
      icon: '👤',
      show:
        hasPermission(PERMISSIONS.CUSTOMERS_BLACKLIST) ||
        hasPermission(PERMISSIONS.MARKETING_PUSH) ||
        hasPermission(PERMISSIONS.MARKETING_PUSH_ANALYTICS),
      children: [
        { name: 'Push Notifications', href: '/marketing/push', icon: '🔔', show: hasPermission(PERMISSIONS.MARKETING_PUSH) },
        { name: 'Push Analytics', href: '/marketing/push/analytics', icon: '📊', show: hasPermission(PERMISSIONS.MARKETING_PUSH_ANALYTICS) },
        { name: 'Customer Blacklist', href: '/customers/blacklist', icon: '🚫', show: hasPermission(PERMISSIONS.CUSTOMERS_BLACKLIST) },
      ],
    },
    {
      name: 'Search',
      icon: '🔎',
      show:
        hasPermission(PERMISSIONS.SEARCH_VIEW_ANALYTICS) ||
        hasPermission(PERMISSIONS.SEARCH_VIEW_MAPPINGS) ||
        hasPermission(PERMISSIONS.SIZE_CATEGORIES_MANAGE) ||
        hasPermission(PERMISSIONS.COLOR_FAMILIES_MANAGE) ||
        hasPermission(PERMISSIONS.SEARCH_MANAGE_FILTERS) ||
        hasPermission(PERMISSIONS.IMAGE_VECTORS_MANAGE) ||
        hasPermission(PERMISSIONS.CATALOGS_MANAGE_RULES),
      children: [
        { name: 'Analytics', href: '/search/analytics', icon: '📊', show: hasPermission(PERMISSIONS.SEARCH_VIEW_ANALYTICS) },
        { name: 'Mappings', href: '/search/mappings', icon: '🔗', show: hasPermission(PERMISSIONS.SEARCH_VIEW_MAPPINGS) },
        { name: 'Filters', href: '/search/filters', icon: '🎛️', show: hasPermission(PERMISSIONS.SEARCH_MANAGE_FILTERS) },
        { name: 'Catalog Rules', href: '/search/catalog-rules', icon: '🗂️', show: hasPermission(PERMISSIONS.CATALOGS_MANAGE_RULES) },
        { name: 'Size Categories', href: '/search/size-categories', icon: '📏', show: hasPermission(PERMISSIONS.SIZE_CATEGORIES_MANAGE) },
        { name: 'Color Families', href: '/search/color-families', icon: '🎨', show: hasPermission(PERMISSIONS.COLOR_FAMILIES_MANAGE) },
        { name: 'Image Vectors', href: '/search/image-vectors', icon: '🧬', show: hasPermission(PERMISSIONS.IMAGE_VECTORS_MANAGE) },
      ],
    },
    { name: 'Users', href: '/users', icon: '👥', show: hasPermission(PERMISSIONS.USERS_VIEW) },
    { name: 'Sellers & Brands', href: '/sellers-management', icon: '🏢', show: hasPermission(PERMISSIONS.SELLERS_VIEW) },
    { name: 'Roles & Permissions', href: '/roles', icon: '🔐', show: hasPermission(PERMISSIONS.ROLES_VIEW) },
    { name: 'QR Code Generator', href: '/qr-code', icon: '📱', show: hasPermission(PERMISSIONS.QRCODE_GENERATE) },
    {
      name: 'Settings',
      icon: '⚙️',
      show: true,
      children: [
        { name: 'Notifications', href: '/notification-settings', icon: '🔔', show: true },
        { name: 'Shipping Defaults', href: '/shipping-defaults', icon: '🚚', show: hasPermission(PERMISSIONS.ORDERS_FULFILL) },
        { name: 'Market Price Uplifts', href: '/market-price-uplifts', icon: '📈', show: hasPermission(PERMISSIONS.MARKET_PRICE_UPLIFTS_MANAGE) },
        { name: 'WhatsApp Button', href: '/whatsapp-button', icon: '💬', show: hasPermission(PERMISSIONS.WHATSAPP_BUTTON_MANAGE) },
        { name: 'Competitor Analysis', href: '/competitor-analysis', icon: '🔍', show: hasPermission(PERMISSIONS.COMPETITOR_ANALYSIS_VIEW) },
        { name: 'Change Password', href: '/change-password', icon: '🔑', show: true },
      ],
    },
  ];

  const visibleNav = navigation.filter((item) => item.show);

  const isActive = (href: string) => location.pathname === href;
  const isChildActive = (item: NavItem): boolean =>
    item.children?.some((child) => child.show && child.href && isActive(child.href)) ?? false;

  // Auto-expand parent if a child is active
  const getExpandedMenus = () => {
    const auto: string[] = [];
    visibleNav.forEach((item) => {
      if (item.children && isChildActive(item)) auto.push(item.name);
    });
    return Array.from(new Set([...expandedMenus, ...auto]));
  };
  const effectiveExpanded = getExpandedMenus();

  return (
    <div className="flex h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`no-print fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Safe area spacer + Logo */}
          <div className="bg-primary-600" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
            <div className="flex items-center justify-between h-16 px-4">
            <Link to="/dashboard" className="flex items-center" onClick={() => setSidebarOpen(false)}>
              <img src="/logo.png" alt="Taminzo" className="h-10 w-auto filter brightness-0 invert" />
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            </div>
          </div>

          {/* User info */}
          <div className="px-4 py-4 border-b border-gray-200">
            {user?.seller ? (
              <>
                <p className="text-sm font-medium text-gray-900">{user.seller.name}</p>
                <p className="text-xs text-gray-500">{user.role?.name}</p>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2 mb-1">
                  <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">T</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">Taminzo Admin</p>
                </div>
                <p className="text-xs text-gray-500 ml-10">{user?.role?.name}</p>
              </>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {visibleNav.map((item) =>
              item.children ? (
                <div key={item.name}>
                  <button
                    onClick={() => toggleMenu(item.name)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      isChildActive(item)
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center">
                      <span className="text-xl mr-3">{item.icon}</span>
                      {item.name}
                    </span>
                    <svg
                      className={`w-4 h-4 transition-transform ${effectiveExpanded.includes(item.name) ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {effectiveExpanded.includes(item.name) && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.filter((c) => c.show).map((child) => (
                        <Link
                          key={child.name}
                          to={child.href!}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            isActive(child.href!)
                              ? 'bg-primary-100 text-primary-700'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <span className="text-lg mr-3">{child.icon}</span>
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href!}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive(item.href!)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-xl mr-3">{item.icon}</span>
                  {item.name}
                </Link>
              ),
            )}
          </nav>

          {/* Logout button */}
          <div className="p-4 border-t border-gray-200" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:pl-64 print:pl-0 min-w-0">
        {/* Top bar for mobile - Fixed and sticky */}
        <div className="no-print lg:hidden bg-white shadow h-16 flex items-center justify-between px-4 sticky top-0 z-10 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <Link to="/dashboard" className="flex items-center">
            <img src="/logo.png" alt="Taminzo" className="h-10 w-auto" />
          </Link>
          <div className="w-6" /> {/* Spacer for centering */}
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-2 sm:p-4 md:p-6">
          <Outlet />
        </main>
        <PWANavBar />
      </div>
    </div>
  );
};
